import { RegistrationState } from "@faro-lotv/service-wires";
import { StatusResultCloudRegistration } from "@api/progress-api/progress-api-types";
import { PublishedData } from "@pages/data-management/published-data/published-data-types";
import { RegisteredData } from "@pages/data-management/registered-data/registered-data-types";
import { isProjectPointCloudCreatorTask } from "@custom-types/sdb-background-tasks-type-guards";
import { isSdbBackgroundTaskOngoing } from "@utils/sdb-background-tasks-utils";
import { SdbBackgroundTask } from "@custom-types/sdb-background-tasks-types";

/**
 * @returns true if the data is currently being published.
 */
export function checkIsPublishing(publishedData: PublishedData): boolean {
  // False if the task property is not defined or if is not an ongoing task.
  return !!publishedData.task && isSdbBackgroundTaskOngoing(publishedData.task);
}

/**
 * @returns time when the most recent published revision was modified.
 *          If no revisions are published, returns 0.
 */
function timePublished(publishedRevisions: PublishedData[]): number {
  const publishedRevision = publishedRevisions.length ? publishedRevisions[0] : undefined;
  return publishedRevision ? new Date(publishedRevision.modifiedAt).getTime() : 0;
}

/**
 * @returns true if the data is published, and has no draft changes or ongoing tasks.
 *          Also checks that the time of publish is after upload and registration.
 */
export function checkIsPublished(
  publishedRevisions: PublishedData[],
  hasAnyDraftChanges: boolean,
  timeUpload: number,
  timeRegister: number,
  backgroundTasks: SdbBackgroundTask[]
): boolean {
  const publishedRevision = publishedRevisions.length ? publishedRevisions[0] : undefined;
  if (!publishedRevision) {
    return false;
  }

  const timePublish = timePublished(publishedRevisions);

  // Also check if there's an ongoing PPC task that is newer than the published revision.
  // Required because revision does not exist yet when task is scheduled so hasAnyDraftChanges is false.
  // Use case: Re-publish feature.
  const ongoingPPCTasks = backgroundTasks
    .filter(isProjectPointCloudCreatorTask)
    .filter(isSdbBackgroundTaskOngoing)
    .filter(
      (task) => task.createdAt > publishedRevision?.createdAt
    );

  return !!publishedRevision && !hasAnyDraftChanges && ongoingPPCTasks.length === 0 &&
    0 < timePublish && timeUpload <= timePublish && timeRegister <= timePublish;
}

/**
 * @returns true if the publishing worker had an error, i.e. PPC task failed.
 */
export function hasPublishError(registeredOrPublishedData: RegisteredData | PublishedData): boolean {
  // Case#1: This check is when PPC worker runs together with Registration. To be removed soon.
  const statusResult = registeredOrPublishedData.task?.status?.result as StatusResultCloudRegistration | undefined;
  if ((registeredOrPublishedData.state === RegistrationState.registered || registeredOrPublishedData.task?.state === "Succeeded") &&
    statusResult?.additionalData?.state === "FAILED" &&
    statusResult?.additionalData?.error === "PUBLISH_FAILED"
  ) {
    return true;
  }

  // Case#2: Check the status of PPC task. This is the only thing to be used in the future.
  if (registeredOrPublishedData.task?.taskType === "ProjectPointCloudCreator" &&
    registeredOrPublishedData.task?.state === "Failed"
  ) {
    return true;
  }

  // No publish errors detected.
  return false;
}
